
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Stack,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Snackbar, 
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  collection,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { firestore } from "../firebase.config";
import PageNavBar from "../components/PageNavBar";
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";


const UserManagement = () => {
  // const {tenantId } = useUserContext();
  const [tenantList, setTenantList] = useState([]);
  //const [selectedTenant, setSelectedTenant] = useState(null);
  //const [selectedTenantName, setSelectedTenantName] = useState("");
  const [userList, setUserList] = useState([]);
  const [form, setForm] = useState({ userName: "", userEmail: "", role: "user" });
  const [allUnassignedUsers, setAllUnassignedUsers] = useState([]);
  const [selectedUnassignedUser, setSelectedUnassignedUser] = useState("");
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [groups, setGroups] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [inactiveExistingUser, setInactiveExistingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info", // success | error | warning | info
  });

  const {
    userRole: contextUserRole,
    tenantId: contextTenantId,
    selectedTenantId: contextSelectedTenantId,
    // setSelectedTenantId,
    // setSelectedTenantName,
  } = useUserContext();

  const [userRole, setUserRole] = useState(
    contextUserRole || sessionStorage.getItem("userRole") || "" );

  const [tenantId, setTenantId] = useState(
    contextTenantId || sessionStorage.getItem("tenantId") || "" );
  
  const [selectedTenant, setSelectedTenant] = useState("");

  useEffect(() => {
    // Always check sessionStorage if context is not ready
    if (!contextUserRole) {
      const storedRole = sessionStorage.getItem("userRole");
      if (storedRole && storedRole !== userRole) {
        setUserRole(storedRole);
      }
    } else if (contextUserRole !== userRole) {
      setUserRole(contextUserRole);
      sessionStorage.setItem("userRole", contextUserRole);
    }
    if (contextTenantId && contextTenantId !== tenantId) {
      setTenantId(contextTenantId);
      sessionStorage.setItem("tenantId", contextTenantId);
    }
    if (contextSelectedTenantId && contextSelectedTenantId !== selectedTenant) {
      setSelectedTenant(contextSelectedTenantId);
      sessionStorage.setItem("selectedTenantId", contextSelectedTenantId);
    }
  }, [contextUserRole, contextTenantId, contextSelectedTenantId]);

  useEffect(() => {
    const fetchGroups = async () => {
      if (!selectedTenant) return;
      const q = query(
        collection(firestore, "Groups"),
        where("tenantId", "==", selectedTenant)
      );
      const snapshot = await getDocs(q);
      const groupList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGroups(groupList);
    };
    fetchGroups();
  }, [selectedTenant]);

  const getUserGroups = (userEmail) => {
    // Find all groups where this user is a member
    const userGroups = groups
      .filter(group => Array.isArray(group.members) && group.members.includes(userEmail))
      .map(group => group.taskName || group.teamName || group.id); // Use group name or id
    return userGroups.length > 0 ? userGroups.join(", ") : "—";
  };

    
  useEffect(() => {
    if (userRole !== "SuperAdmin") {
      setSelectedTenant(tenantId); // Admin gets locked to their own tenant
    }
  }, [userRole, tenantId]);

  useEffect(() => {
    if (userRole === "admin" && tenantId) {
      setSelectedTenant(tenantId);
    }
  }, [userRole, tenantId]);
  


  // Fetch tenants
  useEffect(() => {
    const fetchTenants = async () => {
      setTenantsLoading(true);
      const snapshot = await getDocs(collection(firestore, "TenantMaster"));
      const tenants = snapshot.docs.map((doc) => doc.data());
      setTenantList(tenants);
      setTenantsLoading(false);
    };
    if (userRole === "SuperAdmin") fetchTenants();
  }, [userRole]);

  // Fetch users for selected tenant
  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedTenant) return;
      if (userRole !== "SuperAdmin") {
        // Load tenant name for Admins
        const tenant = tenantList.find((t) => t.tenantId === selectedTenant);
        if (tenant) {
          selectedTenant(tenant.tenantName);
        }
      }
      //console.log("Fetching users for:", selectedTenant); // Debug log
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", selectedTenant)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      //console.log("Fetched users:", users); // Debug log
      setUserList(users);
   // Also update tenant name
      const tenant = tenantList.find((t) => t.tenantId === selectedTenant);
    };
    fetchUsers();
  }, [selectedTenant, tenantList]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      const snapshot = await getDocs(collection(firestore, "Users"));
      const all = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllUsers(all);
    };
    if (userRole === "SuperAdmin") fetchAllUsers();
  }, [userRole]);


  // Fetch users without tenantID
  useEffect(() => {
    if (userRole !== "SuperAdmin") return;

    const fetchUnassignedUsers = async () => {
      const q = query(
        collection(firestore, "Users"),
        where("tenantId", "==", null)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllUnassignedUsers(users);
    };
    fetchUnassignedUsers();
  }, [userRole]);

  // Duplicate handleTenantSelect removed to fix redeclaration error.

  const handleUserSelection = (id) => {
    const selected = allUsers.find((u) => u.id === id);
    setSelectedUserId(id);
    setSelectedUserName(selected?.userName || "");
    setSelectedUserEmail(selected?.userEmail || "");
  };

  const handleAssignUserToTenant = async () => {
    if (!selectedUserId || !selectedTenant) return;

    const selectedUser = allUsers.find((u) => u.id === selectedUserId);
    const tenant = tenantList.find((t) => t.tenantId === selectedTenant);
    try {
      await updateDoc(doc(firestore, "Users", selectedUserId), {
        tenantId: selectedTenant,
        tenantName: selectedTenant,
        Is_Active: true,
      });
      setSelectedUserId("");
      setSelectedUserName("");
      setSelectedUserEmail("");
      setAddUserDialogOpen(false);
      // Refresh user list
      setSelectedTenant((prev) => prev);
    } catch (err) {
      console.error("Failed to assign user:", err);
    }
  };

  if (!userRole || userRole === "") {
    // Only check sessionStorage if contextUserRole is also empty
    if (!contextUserRole) {
      const storedRole = sessionStorage.getItem("userRole");
      if (storedRole) {
        setUserRole(storedRole);
        return <Typography>Loading...</Typography>;
      }
    }
    return <Typography>Loading...</Typography>;
  }
  
  console.log("User Role:", userRole);
  //console.log("Selected Tenant:", selectedTenant);  


  const handleAddOrUpdateUser = async () => {
    if (!form.userName || !form.userEmail || !selectedTenant) {
      return alert("All fields are required");
    }

    const q = query(
      collection(firestore, "Users"),
      where("userEmail", "==", form.userEmail)
    );

    const snapshot = await getDocs(q);

    // 🚫 Email already exists
    if (!isEditing && !snapshot.empty) {
      const existingDoc = snapshot.docs[0];
      const existingUser = { id: existingDoc.id, ...existingDoc.data() };

      if (existingUser.Is_Active === false) {
        // 👉 Show reactivate dialog
        setInactiveExistingUser(existingUser);
        setReactivateDialogOpen(true);
      } else {
        showSnackbar(`User with email ${form.userEmail} already exists.`,"error");
      }
      return;
    }

    // ✅ Normal add flow
    const userData = {
      ...form,
      tenantId: selectedTenant,
      Is_Active: true,
    };

    try {
      if (isEditing && editingId) {
        await updateDoc(doc(firestore, "Users", editingId), userData);
      } else {
        await setDoc(doc(firestore, "Users", form.userEmail), userData);
      }

      setForm({ userName: "", userEmail: "", role: "user" });
      setIsEditing(false);
      setEditingId(null);
      setAddUserDialogOpen(false);
      setSelectedTenant((prev) => prev);
    } catch (err) {
      console.error("Error adding/updating user", err);
    }
  };

  const handleTenantSelect = (e) => {
    const id = e.target.value;
    setSelectedTenant(id);
    const tenant = tenantList.find((t) => t.tenantId === id);
    const name = tenant?.tenantName || "";
  };

  const handleEdit = (user) => {
    setForm({ 
      userName: user.userName, 
      userEmail: user.userEmail, 
      role: user.role || "user",
    });
    setIsEditing(true);
    setEditingId(user.userEmail); // use email as ID
    setAddUserDialogOpen(true); // open dialog for editing
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    try {
      await deleteDoc(doc(firestore, "Users", userToDelete.id));

      setUserList((prev) =>
        prev.filter((u) => u.id !== userToDelete.id)
      );

      showSnackbar("User deleted successfully", "success");
    } catch (err) {
      console.error("Failed to delete user", err);
      showSnackbar("Failed to delete user", "error");
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleUserActiveStatus = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(firestore, "Users", userId), {
        Is_Active: !currentStatus,
      });

      setUserList((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, Is_Active: !currentStatus } : u
        )
      );
    } catch (err) {
      console.error("Failed to update user status", err);
      alert("Failed to update user status");
    }
  };  


  const handleStatusClick = (user) => {
    setSelectedUser(user);
    setConfirmOpen(true);
  };


  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", position: "relative" }}>
      <PageNavBar title="User Management" backTo="/dashboard" />
      <TenantBanner />
      <Box p={4}>

      <Typography variant="h4" mb={3}>
        User Management ({userRole})
      </Typography>

      {/* Tenant Selector */}
      {userRole === "SuperAdmin" && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Tenant</InputLabel>
            <Select
              value={selectedTenant || ""}
              // onChange={(e) => setSelectedTenant(e.target.value)}
              onChange={handleTenantSelect}
              label="Select Tenant"
            >
            {tenantList.map((tenant) => (
            <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
              {tenant.tenantName}
            </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Tenant selected but no users */}
      {selectedTenant && userList.length === 0 && (
        <Typography>No users found for this tenant.</Typography>
      )}


    {/* User List */}
    {selectedTenant && userList.length > 0 && (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Users in {selectedTenant || "your Tenant"}
      </Typography>

    <Paper sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ backgroundColor: "#f0f0f0" }}>
          <tr>
            {/* <th style={{ padding: "10px", border: "1px solid #ddd" }}>User ID</th> */}
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>User Name</th>
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>User Group</th>
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>User Email ID</th>
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Role</th>
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Status</th>
            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {userList.map((user) => (
            <tr key={user.id}  style={{ backgroundColor: user.Is_Active ? "#ffffff" : "#f2f2f2",color: user.Is_Active ? "inherit" : "#9e9e9e",}}>
              {/* <td style={{ padding: "10px", border: "1px solid #ddd" }}>{user.id}</td> */}
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>{user.userName}</td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>{getUserGroups(user.userEmail) || "—"}</td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>{user.userEmail}</td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>{user.role}</td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch
                    checked={user.Is_Active}
                    color="success"
                    onChange={() => handleStatusClick(user)}
                  />
                  <Typography variant="body2">
                    {user.Is_Active ? "Active" : "Inactive"}
                  </Typography>
                </Stack>
              </td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                <Stack direction="row" spacing={1}>
                  <IconButton variant="outlined" size="small" onClick={() => handleEdit(user)} color="primary" disabled={!user.Is_Active}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(user)}
                    sx={{
                      color: "error.main",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                      },
                    }}
                  >                 
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Paper>
  </Box>
)}

    {/* Add/Edit Form */}
      {selectedTenant && (
        <Paper sx={{ p: 5, mb: 3 }}>
        </Paper>
      )}

    {/* ➕ Add user button for SuperAdmin */}
      {selectedTenant && (
        <>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: "fixed", bottom: 24, right: 24 }}
            onClick={() => setAddUserDialogOpen(true)}
          >
            <AddIcon />
          </Fab>

          <Dialog 
            open={addUserDialogOpen} onClose={() => {
              setAddUserDialogOpen(false);
              setIsEditing(false);
              setForm({ userName: "", userEmail: "", role: "user" });
            }}
            maxWidth="sm"
            fullWidth
          >            
            <DialogTitle>{isEditing ? "Edit User" : `Add Users in ${selectedTenant || tenantId || "your tenant"}`}</DialogTitle>
            <DialogContent>
              <Stack spacing={4} sx={{ mt: 1 }}>
                <TextField
                  label="User Name"
                  value={form.userName}
                  onChange={(e) => setForm({ ...form, userName: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="User Email"
                  value={form.userEmail}
                  onChange={(e) => setForm({ ...form, userEmail: e.target.value })}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={form.role}
                    label="Role"
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="supervisor">Supervisor</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setAddUserDialogOpen(false);
                setIsEditing(false);
                setForm({ userName: "", userEmail: "", role: "user" });
              }}>Cancel</Button>
              <Button variant="contained" onClick={handleAddOrUpdateUser}>
                {isEditing ? "Update User" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          Confirm Status Change
        </DialogTitle>

        <DialogContent>
          <Typography>
            {selectedUser?.Is_Active ? (
              <>
                Are you sure you want to <b>deactivate</b> the user{" "}
                <b>{selectedUser?.userName}</b>? <br />
                The user will not be able to log in.
              </>
            ) : (
              <>
                Are you sure you want to <b>activate</b> the user{" "}
                <b>{selectedUser?.userName}</b>? <br />
                The user will be able to log in.
              </>
            )}
          </Typography>
        </DialogContent>

            
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
            
          <Button
            color={selectedUser?.Is_Active ? "error" : "success"}
            variant="contained"
            onClick={() => {
              toggleUserActiveStatus(
                selectedUser.id,
                selectedUser.Is_Active
              );
              setConfirmOpen(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={reactivateDialogOpen}
        onClose={() => setReactivateDialogOpen(false)}
      >     
        <DialogTitle>User Already Exists</DialogTitle>

        <DialogContent>
          <Typography>
            User <b>{inactiveExistingUser?.userName}</b> with email{" "}
            <b>{inactiveExistingUser?.userEmail}</b> already exists but is{" "}
            <b>inactive</b>.
            <br /><br />
            Would you like to activate this user?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setReactivateDialogOpen(false);
              setInactiveExistingUser(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            color="success"
            onClick={async () => {
              await updateDoc(
                doc(firestore, "Users", inactiveExistingUser.id),
                { Is_Active: true }
              );
            
              setUserList((prev) =>
                prev.map((u) =>
                  u.id === inactiveExistingUser.id
                    ? { ...u, Is_Active: true }
                    : u
                )
              );
            
              setReactivateDialogOpen(false);
              setInactiveExistingUser(null);
              setAddUserDialogOpen(false);
            }}
          >
            Activate User
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >     
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >     
        <DialogTitle sx={{ color: "error.main" }}>
          Confirm Deletion
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete the user{" "}
            <b>{userToDelete?.userName}</b>?
            <br />
            <br />
            Email: <b>{userToDelete?.userEmail}</b>
            <br />
            <br />
            <span style={{ color: "#d32f2f" }}>
              This action cannot be undone.
            </span>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
            }}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteUser}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
// Table styles
const thStyle = { padding: "10px", border: "1px solid #ddd" };
const tdStyle = { padding: "10px", border: "1px solid #ddd" };

export default UserManagement;
